import { useMemo, useState, useRef, useEffect } from 'react';
import useIsMobile from '../../utils/windows';
import styles from './Widget.module.css';

const DEFAULT_MAX_BTNS = 5;

function buildPageWindow(current, total, maxBtns = DEFAULT_MAX_BTNS) {
    if (total <= maxBtns) return Array.from({ length: total }, (_, i) => i + 1);
    const half = Math.floor(maxBtns / 2);
    let start = Math.max(1, current - half);
    let end = Math.min(total, start + maxBtns - 1);
    if (end - start + 1 < maxBtns) start = Math.max(1, end - maxBtns + 1);

    const items = [];
    if (start > 1 && start > 2) items.push('left-ellipsis');
    for (let n = start; n <= end; n++) items.push(n);
    if (end < total && end < total - 1) items.push('right-ellipsis');
    return items;
}

export default function TableCard({
    data,
    columns,
    rows = 10,
    pagenateSize = 10,
    rowKey,
    total,
    page,
    onPageChange,
    loading,
    maxPageButtons = DEFAULT_MAX_BTNS,
    onReplay,
    justListNotTitle = false,
    rowKeyField = 'key',
    selectedKeys = new Set(),
    onToggleOne,
    onToggleAll,
    onRowClick,
}) {
    const serverPaginated = typeof total === 'number' && typeof page === 'number';
    const pageSize = pagenateSize;

    const totalRows = serverPaginated ? total : data?.length || 0;
    const totalPages = Math.max(1, Math.ceil(totalRows / Math.max(1, pageSize)));
    const paginate = totalRows > pageSize;

    const [innerPage, setInnerPage] = useState(1);
    const currentPage = serverPaginated ? page : innerPage;

    const visible = serverPaginated
        ? (data ?? [])
        : (() => {
              const start = (currentPage - 1) * pageSize;
              return paginate ? (data ?? []).slice(start, start + pageSize) : (data ?? []);
          })();

    // 🔹 현재 페이지의 key 목록
    const visibleRowKeys = useMemo(() => visible.map((r) => r?.[rowKeyField]), [visible, rowKeyField]);

    // 🔹 전체 체크 상태 계산
    const allChecked = useMemo(() => visibleRowKeys.length > 0 && visibleRowKeys.every((k) => selectedKeys.has(k)), [visibleRowKeys, selectedKeys]);

    const someChecked = useMemo(() => visibleRowKeys.length > 0 && !allChecked && visibleRowKeys.some((k) => selectedKeys.has(k)), [visibleRowKeys, selectedKeys, allChecked]);

    const headerCheckRef = useRef(null);
    useEffect(() => {
        if (headerCheckRef.current) headerCheckRef.current.indeterminate = someChecked;
    }, [someChecked]);

    const pageWindow = useMemo(() => buildPageWindow(currentPage, totalPages, maxPageButtons), [currentPage, totalPages, maxPageButtons]);

    const setPage = (next) => {
        if (next < 1 || next > totalPages) return;
        if (serverPaginated) onPageChange?.(next);
        else setInnerPage(next);
    };

    const scrollBody = (paginate ? pageSize : visible.length) > rows;
    const paddingHeader = scrollBody ? 24 : 15;

    const isMobile = useIsMobile(768);
    const colTemplate = useMemo(
        () =>
            columns
                .map((c) => {
                    const w = isMobile && c.mobileWidth ? c.mobileWidth : c.width;
                    return w || '1fr';
                })
                .join(' '),
        [columns, isMobile]
    );

    // 🔹 행 렌더 함수
    const renderRow = (row, i) => {
        const rowId = rowKey ? rowKey(row, i) : (row?.[columns[0]?.key] ?? `${i}`);
        const entityKey = row?.[rowKeyField];

        const handleRowClick = () => {
            if (typeof onRowClick === 'function') {
                onRowClick(row, i);
            }
        };

        return (
            <div
                key={rowId}
                className={`${styles.table__row} ${onRowClick ? styles.table__rowClickable : ''}`}
                style={{ display: 'grid', gridTemplateColumns: colTemplate }}
                // ✅ 행 전체 클릭 시
                onClick={handleRowClick}
                // 키보드 접근성 (Enter/Space)
                tabIndex={onRowClick ? 0 : -1}
                onKeyDown={(e) => {
                    if (!onRowClick) return;
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleRowClick();
                    }
                }}
            >
                {columns.map((col) => {
                    const baseStyle = col.align ? { textAlign: col.align } : {};
                    const w = isMobile && col.mobileWidth ? col.mobileWidth : col.width;
                    const needsMin = w && !w.endsWith('px');
                    const style = needsMin ? { ...baseStyle, minWidth: col.minWidth } : baseStyle;

                    if (col.type === 'check') {
                        return (
                            <div key={col.key} style={{ ...style, padding: 0 }}>
                                <input
                                    type="checkbox"
                                    checked={selectedKeys.has(entityKey)}
                                    onChange={(e) => {
                                        e.stopPropagation(); // ✅ 행 클릭으로 안 번지게
                                        onToggleOne?.(entityKey);
                                    }}
                                    aria-label="선택"
                                />
                            </div>
                        );
                    }

                    if (col.type === 'button') {
                        const label = col.buttonLabel || col.header || '버튼';
                        const enabledFlag = row?.[col.key];
                        const disabledFlag = row?.[`${col.key}Disabled`];
                        const disabled = disabledFlag === true ? true : enabledFlag === false;

                        const handleClick = (e) => {
                            e.stopPropagation(); // ✅ 행 클릭과 분리
                            if (typeof col.onClick === 'function') return col.onClick(row, i);
                            if (col.key === 'replay' && typeof onReplay === 'function') return onReplay(row, i);
                        };

                        return (
                            <div key={col.key} style={{ ...style, padding: 0 }}>
                                <button className={styles.btn} disabled={!!disabled || loading} onClick={handleClick}>
                                    {label}
                                </button>
                            </div>
                        );
                    }

                    const value =
                        typeof col.accessor === 'function' ? col.accessor(row) : (col.accessor || col.key).split('.').reduce((acc, k) => (acc == null ? acc : acc[k]), row);

                    return (
                        <div key={col.key} style={style}>
                            {value}
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <div className={styles.table__scrollOuter}>
            <div className={`${styles.table} ${styles.table__vsep}`} style={{ '--cols': colTemplate }}>
                {/* 🔹 헤더 */}
                <div
                    className={justListNotTitle ? styles.table__head_round : styles.table__head}
                    style={{
                        display: 'grid',
                        gridTemplateColumns: colTemplate,
                        paddingRight: paddingHeader,
                    }}
                >
                    {columns.map((col) => {
                        const style = col.align ? { textAlign: col.align } : undefined;

                        if (col.type === 'check') {
                            return (
                                <div key={col.key} style={style}>
                                    <input
                                        type="checkbox"
                                        ref={headerCheckRef}
                                        checked={allChecked}
                                        onChange={(e) => {
                                            e.stopPropagation();
                                            onToggleAll?.(visibleRowKeys);
                                        }}
                                        aria-label="전체 선택"
                                    />
                                </div>
                            );
                        }

                        return (
                            <div key={col.key} style={style}>
                                {col.header}
                            </div>
                        );
                    })}
                </div>

                {/* 🔹 바디 */}
                <div className={`${styles.table__body} ${scrollBody ? styles['table__body--scroll'] : ''}`} style={scrollBody ? { '--rows': rows } : undefined}>
                    {(() => {
                        const hasData = visible.length > 0;

                        if (loading && hasData) return visible.map(renderRow);
                        if (loading && !hasData) return <div className={styles.table__row}>로딩 중…</div>;
                        if (!loading && !hasData)
                            return (
                                <div className={styles.table__row}>
                                    {columns.map((col) => (
                                        <div key={col.key}>{col.key === 'name' || col.key === 'date' ? '데이터가 없습니다' : ''}</div>
                                    ))}
                                </div>
                            );

                        return visible.map(renderRow);
                    })()}
                </div>

                {/* 🔹 페이지네이션 */}
                {paginate ? (
                    <div className={styles.pager} role="navigation" aria-label="페이지네이션">
                        <button className={styles.pager__btn} onClick={() => setPage(1)} disabled={currentPage === 1}>
                            «
                        </button>
                        <button className={styles.pager__btn} onClick={() => setPage(currentPage - 1)} disabled={currentPage === 1}>
                            ‹
                        </button>

                        {pageWindow.map((it, idx) =>
                            typeof it === 'number' ? (
                                <button
                                    key={it}
                                    className={styles.pager__num}
                                    onClick={() => setPage(it)}
                                    aria-current={currentPage === it ? 'page' : undefined}
                                    disabled={loading}
                                >
                                    {it}
                                </button>
                            ) : (
                                <span key={`${it}-${idx}`} className={styles.pager__ellipsis}>
                                    …
                                </span>
                            )
                        )}

                        <button className={styles.pager__btn} onClick={() => setPage(currentPage + 1)} disabled={currentPage === totalPages}>
                            ›
                        </button>
                        <button className={styles.pager__btn} onClick={() => setPage(totalPages)} disabled={currentPage === totalPages}>
                            »
                        </button>
                    </div>
                ) : (
                    <div className={styles.table__footer} />
                )}
            </div>
        </div>
    );
}
